import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * User Registration Status Service
 * 
 * This service manages user registration status tracking and determines
 * when notification permissions should be requested based on:
 * 1. PWA installation status
 * 2. Successful event registration completion
 * 
 * Key Features:
 * - Tracks user registration completion across events
 * - Manages notification eligibility criteria
 * - Persistent storage of user interaction states
 * - Integration with PWA installation status
 */

/**
 * Check if user has successfully registered for any event
 * This determines eligibility for notification permission requests
 * 
 * @param {string} userId - The Firebase user ID
 * @returns {Promise<Object>} Registration status information
 */
export const checkUserRegistrationStatus = async (userId) => {
  try {
    if (!userId) {
      return { 
        hasSuccessfulRegistration: false, 
        registrationCount: 0,
        error: 'No user ID provided'
      };
    }

    // Query all collections for registrations by this user
    // Check across all event registrations for successful payments
    const eventsSnapshot = await getDocs(collection(db, 'events'));
    let successfulRegistrations = 0;
    let totalRegistrations = 0;
    const registrationDetails = [];

    for (const eventDoc of eventsSnapshot.docs) {
      const eventId = eventDoc.id;
      const eventData = eventDoc.data();
      
      // Query registrations for this event by this user
      const registrationsRef = collection(db, `registrations/${eventId}/registrations`);
      const userRegistrationsQuery = query(
        registrationsRef,
        where('userId', '==', userId)
      );
      
      const userRegistrationsSnapshot = await getDocs(userRegistrationsQuery);
      
      // Use for...of loop instead of forEach to avoid unsafe references in function declared inside loop
      for (const regDoc of userRegistrationsSnapshot.docs) {
        const registrationData = regDoc.data();
        totalRegistrations++;
        
        // Check if this is a successful registration
        if (registrationData.status === 'success' && 
            registrationData.paymentDetails?.transactionId) {
          successfulRegistrations++;
          registrationDetails.push({
            eventId,
            eventTitle: eventData.title || eventData.eventTitle,
            registrationId: regDoc.id,
            registrationDate: registrationData.timestamp,
            paymentAmount: registrationData.paymentDetails?.totalAmount
          });
        }
      }
    }

    return {
      hasSuccessfulRegistration: successfulRegistrations > 0,
      registrationCount: successfulRegistrations,
      totalRegistrations,
      registrationDetails,
      lastChecked: new Date().toISOString()
    };

  } catch (error) {
    console.error('Error checking user registration status:', error);
    return {
      hasSuccessfulRegistration: false,
      registrationCount: 0,
      error: error.message
    };
  }
};

/**
 * Check if user is eligible for notification permission request
 * Based on PWA installation and successful event registration
 * 
 * @param {string} userId - The Firebase user ID
 * @param {boolean} isPWAInstalled - Whether the PWA is installed
 * @returns {Promise<Object>} Eligibility status and details
 */
export const checkNotificationEligibility = async (userId, isPWAInstalled) => {
  try {
    // First check if PWA is installed (requirement #1)
    if (!isPWAInstalled) {
      return {
        eligible: false,
        reason: 'PWA not installed',
        requiresPWAInstall: true,
        requiresRegistration: false
      };
    }

    // Check if user has successful registrations (requirement #2)
    const registrationStatus = await checkUserRegistrationStatus(userId);
    
    if (!registrationStatus.hasSuccessfulRegistration) {
      return {
        eligible: false,
        reason: 'No successful event registrations',
        requiresPWAInstall: false,
        requiresRegistration: true,
        registrationStatus
      };
    }

    // Check if user has already been asked about notifications
    const notificationPrefs = await getUserNotificationPreferences(userId);
    
    if (notificationPrefs.askedBefore && notificationPrefs.response === 'denied') {
      return {
        eligible: false,
        reason: 'User previously denied notifications',
        requiresPWAInstall: false,
        requiresRegistration: false,
        previouslyDenied: true
      };
    }

    if (notificationPrefs.askedBefore && notificationPrefs.response === 'granted') {
      return {
        eligible: false,
        reason: 'Notifications already granted',
        requiresPWAInstall: false,
        requiresRegistration: false,
        alreadyGranted: true
      };
    }

    // User is eligible for notification permission request
    return {
      eligible: true,
      reason: 'All criteria met',
      registrationStatus,
      notificationPrefs
    };

  } catch (error) {
    console.error('Error checking notification eligibility:', error);
    return {
      eligible: false,
      reason: 'Error checking eligibility',
      error: error.message
    };
  }
};

/**
 * Get user's notification preferences and interaction history
 * 
 * @param {string} userId - The Firebase user ID
 * @returns {Promise<Object>} User notification preferences
 */
export const getUserNotificationPreferences = async (userId) => {
  try {
    const prefsDoc = await getDoc(doc(db, 'userNotificationPrefs', userId));
    
    if (prefsDoc.exists()) {
      return prefsDoc.data();
    }

    // Return default preferences if none exist
    return {
      askedBefore: false,
      response: null,
      askedAt: null,
      fcmToken: null,
      enabled: false
    };

  } catch (error) {
    console.error('Error getting notification preferences:', error);
    return {
      askedBefore: false,
      response: null,
      error: error.message
    };
  }
};

/**
 * Save user's notification preference response
 * 
 * @param {string} userId - The Firebase user ID
 * @param {string} response - User's response ('granted', 'denied', 'default')
 * @param {string} fcmToken - FCM token if permission granted
 * @returns {Promise<boolean>} Success status
 */
export const saveNotificationPreference = async (userId, response, fcmToken = null) => {
  try {
    const preferences = {
      askedBefore: true,
      response,
      askedAt: new Date().toISOString(),
      fcmToken,
      enabled: response === 'granted',
      updatedAt: new Date().toISOString()
    };

    await setDoc(doc(db, 'userNotificationPrefs', userId), preferences, { merge: true });
    
    console.log('Notification preferences saved:', preferences);
    return true;

  } catch (error) {
    console.error('Error saving notification preferences:', error);
    return false;
  }
};

/**
 * Mark that a user has completed a successful registration
 * This can trigger notification eligibility checks
 * 
 * @param {string} userId - The Firebase user ID
 * @param {string} eventId - The event ID
 * @param {string} registrationId - The registration ID
 * @returns {Promise<boolean>} Success status
 */
export const markSuccessfulRegistration = async (userId, eventId, registrationId) => {
  try {
    const registrationRecord = {
      userId,
      eventId,
      registrationId,
      completedAt: new Date().toISOString(),
      notificationEligible: true
    };

    await setDoc(
      doc(db, 'userRegistrationHistory', `${userId}_${eventId}`), 
      registrationRecord, 
      { merge: true }
    );

    console.log('Successful registration marked:', registrationRecord);
    return true;

  } catch (error) {
    console.error('Error marking successful registration:', error);
    return false;
  }
};

/**
 * Check if enough time has passed since last notification permission request
 * Implements polite timing to avoid spam
 * 
 * @param {string} userId - The Firebase user ID
 * @param {number} cooldownHours - Hours to wait between requests (default: 24)
 * @returns {Promise<boolean>} Whether enough time has passed
 */
export const canRequestNotificationPermission = async (userId, cooldownHours = 24) => {
  try {
    const prefs = await getUserNotificationPreferences(userId);
    
    if (!prefs.askedAt) {
      return true; // Never asked before
    }

    const lastAskedTime = new Date(prefs.askedAt);
    const now = new Date();
    const hoursSinceLastRequest = (now - lastAskedTime) / (1000 * 60 * 60);

    return hoursSinceLastRequest >= cooldownHours;

  } catch (error) {
    console.error('Error checking notification cooldown:', error);
    return false;
  }
}; 